/**
 * OpenTable, conectado una vez para todo el sitio.
 *
 * El restaurante se escribe aquí —su ID, o el código o el enlace del widget,
 * de donde se saca— y queda en los ajustes del sitio (`siteSettings.openTable`).
 * El editor lo lee al abrir el proyecto: todos los widgets de OpenTable usan
 * ese restaurante, los nuevos nacen ya conectados, y en su panel el ID sale
 * bloqueado. Un solo sitio donde cambiarlo, en vez de uno por widget.
 *
 * No es una credencial: el ID de un restaurante es público (va en su enlace de
 * reservas), así que se enseña tal cual y no pasa por las integraciones con
 * clave cifrada como Omnisend.
 */
import { useEffect, useState } from 'react'
import { IconCheck, IconExternalLink, IconListCheck, IconPlugConnected } from '@tabler/icons-react'
import { OpenTableMark } from './OpenTableMark'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { getProjectSiteSettings, updateSiteSettings } from '../../../lib/api'
import { useI18n, translateServerError } from '../../../lib/i18n'

const OPENTABLE_GUEST_CENTER = 'https://guestcenter.opentable.com/'

/**
 * Los IDs que trae lo escrito: el número (o varios, con comas), o el código o
 * el enlace del widget (`rid=…`, `restaurantIds=…`). La misma lectura que el
 * editor (`openTableWidgetFrom`), reducida a lo que aquí importa.
 */
export function openTableIdsFrom(raw: string): string[] {
  const text = raw.trim().replace(/&amp;/g, '&')
  if (!text) return []
  if (/^\d{1,10}(\s*,\s*\d{1,10})*$/.test(text)) return [...new Set(text.split(/\s*,\s*/))].slice(0, 20)
  const ids = [...text.matchAll(/(?:rid|restaurantIds)=([\d,]+)/gi)]
    .flatMap((match) => (match[1] ?? '').split(','))
    .filter((id) => /^\d{1,10}$/.test(id))
  return [...new Set(ids)].slice(0, 20)
}

export function OpenTableSettings({ projectId, token, projectRole = 'owner', onBack }: {
  projectId: string
  token: string
  projectRole?: ProjectAccessRole
  onBack?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const canManage = can(projectRole, 'project.settings')
  const [saved, setSaved] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  useEffect(() => {
    let alive = true
    getProjectSiteSettings(token, projectId)
      .then((project) => {
        if (!alive) return
        setSaved(project.siteSettings?.openTable?.rids ?? null)
      })
      .catch(() => { if (alive) setSaved(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [projectId, token])

  const detected = openTableIdsFrom(draft)
  const connected = Boolean(saved?.length)

  async function save(rids: string[] | null) {
    setSaving(true)
    setError(null)
    try {
      const next = await updateSiteSettings(token, projectId, { openTable: rids ? { rids } : null })
      setSaved(next.openTable?.rids ?? null)
      setDraft('')
      setConfirmingDisconnect(false)
    } catch (err) {
      setError(translateServerError(err, t('otset.saveFailed')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ProjectSettingsLayout title={t('section.openTable')} description={t('otset.description')} onBack={onBack}>
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon dash-settings-brand-icon is-opentable" aria-hidden="true">
            <OpenTableMark size={26} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t(loading ? 'otset.checking' : connected ? 'otset.connected' : 'otset.notConnected')}</h3>
            <p>
              {loading
                ? t('otset.checkingBody')
                : connected
                  ? t('otset.connectedBody', { id: saved!.join(', ') })
                  : t('otset.emptyBody')}
            </p>
          </div>
          {connected && !loading ? (
            <span className="dash-connect-status is-ok">
              <IconCheck size={13} aria-hidden="true" />
              {t('otset.statusOk')}
            </span>
          ) : null}
        </div>

        {canManage ? (
          <>
            <label className="dash-field">
              <span>{t(connected ? 'otset.changeId' : 'otset.idLabel')}</span>
              <input
                value={draft}
                autoComplete="off"
                spellCheck={false}
                placeholder={t('otset.placeholder')}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter' && detected.length) void save(detected) }}
                disabled={saving}
              />
            </label>
            <p className="dash-card-caption">
              {draft.trim()
                ? (detected.length ? t('otset.detected', { id: detected.join(', ') }) : t('otset.notDetected'))
                : t('otset.where')}{' '}
              <a href={OPENTABLE_GUEST_CENTER} target="_blank" rel="noopener noreferrer">
                {t('otset.openGuestCenter')} <IconExternalLink size={12} aria-hidden="true" />
              </a>
            </p>

            {error ? <div className="dash-inline-msg"><span>{error}</span></div> : null}

            <div className="dash-settings-card-foot">
              <button
                type="button"
                className="dash-primary-btn"
                onClick={() => void save(detected)}
                disabled={saving || detected.length === 0}
              >
                <IconPlugConnected size={14} aria-hidden="true" />
                {t(connected ? 'otset.saveNew' : 'otset.connect')}
              </button>
              {connected ? (
                confirmingDisconnect ? (
                  <>
                    <button type="button" className="dash-danger-btn" onClick={() => void save(null)} disabled={saving}>
                      {t('otset.confirmDisconnect')}
                    </button>
                    <button type="button" className="dash-ghost-btn" onClick={() => setConfirmingDisconnect(false)} disabled={saving}>
                      {t('otset.cancel')}
                    </button>
                  </>
                ) : (
                  <button type="button" className="dash-secondary-btn" onClick={() => setConfirmingDisconnect(true)} disabled={saving}>
                    {t('otset.disconnect')}
                  </button>
                )
              ) : null}
            </div>
            {confirmingDisconnect ? <p className="dash-card-caption">{t('otset.disconnectNote')}</p> : null}
          </>
        ) : (
          <p className="dash-card-caption">{t('otset.adminOnly')}</p>
        )}
      </section>

      {/* Qué hace conectarlo, dicho en la misma pantalla: no pone nada en la
          página por sí solo, sólo deja listos los widgets y botones. */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconListCheck size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('otset.whatTitle')}</h3>
          </div>
        </div>
        <ol className="dash-connect-steps">
          <li><span className="dash-connect-step-num">1</span>{t('otset.what1')}</li>
          <li><span className="dash-connect-step-num">2</span>{t('otset.what2')}</li>
          <li><span className="dash-connect-step-num">3</span>{t('otset.what3')}</li>
        </ol>
      </section>
    </ProjectSettingsLayout>
  )
}
